/**
 * PersonalElinsInsightsPanel — the RIGHT column when view === "personal".
 *
 * Sections 2, 3 and 4: Attractor State, Collapse Risk (P0-P3), Field
 * Weather. These are built and working in routes/PersonalElins.tsx and
 * have never appeared in the member product — this is the first surface
 * that puts them in front of a member.
 *
 * ★ Imported, not rebuilt. Same three components the staging route
 * renders, reading the same envelope from the personal slice.
 *
 * #23 W2 -- when a relationship is selected, a RELATIONSHIP header sits
 * above the run panels: its name, how many turns it has saved, the trust
 * signal AS THE BACKEND RETURNS IT ("no_prior_yet" at n=0 is a string,
 * never 0.0; a value carries a direction only from the second scored turn),
 * the last sealed timestamp, and the windowed turns with their seal /
 * observe stamps. The run panels below show THIS relationship's last run,
 * or say there is none yet.
 */
import { useCockpit } from "../../state/cockpitStore";
import type { BearingsHeader, TurnRecord } from "../../lib/api";
import { trustLine } from "../../lib/trustSignal";
import {
  SectionAttractor,
  SectionCollapseRisk,
  SectionFieldWeather,
} from "../../routes/PersonalElins";

/** ts_* are nanoseconds (time.time_ns). */
function stamp(ns: number | null | undefined): string {
  if (typeof ns !== "number" || !Number.isFinite(ns)) return "—";
  return new Date(Math.floor(ns / 1e6)).toISOString().slice(0, 16).replace("T", " ");
}

function lastSealed(turns: TurnRecord[]): string {
  if (!turns.length) return "—";
  return stamp(Math.max(...turns.map((t) => t.ts_sealed ?? 0)));
}

/** #114 -- the five, in the ruling's order. */
export const PHYSICS_BEARINGS = ["trust", "alignment", "boundary", "agency", "distance"] as const;

/** #114 -- the header as one line: "trust: low (2 of 3) · alignment: split
 *  (1 of 3) · boundary: —". A bearing the header does not carry reads a
 *  dash; no header at all reads a dash. */
export function bearingsLine(h: BearingsHeader | null | undefined): string {
  if (!h) return "—";
  return PHYSICS_BEARINGS.map((b) => {
    const m = h[b];
    return m ? `${b}: ${m.value} (${m.count} of ${m.of_n})` : `${b}: —`;
  }).join(" · ");
}

/** #114 -- the age of the header, in TURNS, never a clock. */
export function bearingsAge(h: BearingsHeader | null | undefined): string {
  if (!h?.age) return "—";
  return `sealed turn ${h.age.sealed_turn} · now turn ${h.age.now_turn}`;
}

/** #114 -- one turn's sealed bearings, the five in order, a dash where the
 *  run returned nothing for that key. */
export function turnBearings(t: TurnRecord): string {
  const b = t.bearings ?? {};
  return PHYSICS_BEARINGS.map((k) => `${k} ${typeof b[k] === "string" && b[k] ? b[k] : "—"}`).join(" · ");
}

export default function PersonalElinsInsightsPanel() {
  const personal = useCockpit((s) => s.personal);
  const rel = useCockpit((s) => s.relationships);
  const activeId = rel.activeId;
  const active = activeId ? rel.items.find((t) => t.thread_id === activeId) ?? null : null;
  const detail = activeId ? rel.detail[activeId] ?? null : null;
  const noRunYet =
    !!activeId && personal.status !== "loading" && personal.status !== "error"
    && !personal.ep && !personal.elins;

  return (
    <section className="cv2-panel cv2-panel-insights">
      <header className="cv2-panel-head">Insights</header>
      <div className="cv2-panel-body">
        {activeId && (
          <div className="cv2-rel-head" data-testid="relationship-header">
            <div className="cv2-mono" data-testid="rel-name">
              {active?.title || activeId}
            </div>
            {!detail && rel.detailStatus === "loading" && (
              <p className="cv2-muted">reading what it saved…</p>
            )}
            {!detail && rel.detailStatus === "error" && (
              <p className="cv2-err">{rel.detailError}</p>
            )}
            {detail && (
              <>
                <dl className="cv2-kv">
                  <div className="cv2-kv-row">
                    <dt>turns</dt>
                    <dd className="cv2-mono" data-testid="rel-turn-count">{detail.turn_count}</dd>
                  </div>
                  <div className="cv2-kv-row">
                    <dt>trust</dt>
                    {/* #167c -- ONE trust line (lib/trustSignal.trustLine); the
                        basin_hop rail row that read the same object is gone. */}
                    <dd className="cv2-mono" data-testid="rel-trust" title="trust_signal.status · .value · .scored_turns · .theta_ready · .theta_floor">{trustLine(detail.trust_signal)}</dd>
                  </div>
                  <div className="cv2-kv-row">
                    <dt>last sealed</dt>
                    <dd className="cv2-mono" data-testid="rel-last-sealed">{lastSealed(detail.turns)}</dd>
                  </div>
                  {/* #114 -- the five bearings, modal over the last 3 physics
                      runs, as the backend serves them; the age in turns. */}
                  <div className="cv2-kv-row" title="bearings_header.<bearing>.value · .count · .of_n">
                    <dt>bearings</dt>
                    <dd className="cv2-mono" data-testid="rel-bearings">{bearingsLine(detail.bearings_header)}</dd>
                  </div>
                  <div className="cv2-kv-row" title="bearings_header.age.sealed_turn · bearings_header.age.now_turn">
                    <dt>bearings age</dt>
                    <dd className="cv2-mono" data-testid="rel-bearings-age">{bearingsAge(detail.bearings_header)}</dd>
                  </div>
                </dl>
                {detail.turns.length > 0 && (
                  <ul className="cv2-rel-turns" data-testid="rel-turns">
                    {detail.turns.map((t) => (
                      <li key={`${t.turn_index}-${t.ts_sealed}`} className="cv2-muted">
                        #{t.turn_index} · sealed {stamp(t.ts_sealed)} ·{" "}
                        {t.ts_observed == null ? "awaiting return" : `observed ${stamp(t.ts_observed)}`}
                        {/* #114 -- the five, only on a turn that carries them */}
                        {t.bearings ? (
                          <span data-testid="rel-turn-bearings" title="turns[].bearings · turns[].run_id">
                            {" · "}{turnBearings(t)}{t.run_id != null ? ` · run ${t.run_id}` : ""}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
        {personal.status === "loading" && !personal.elins && (
          <p className="cv2-muted">running…</p>
        )}
        {noRunYet && (
          <p className="cv2-muted" data-testid="no-run-yet">
            No run yet on this relationship. Run the seed to read it.
          </p>
        )}
        <SectionAttractor elins={personal.elins} />
        {/* #238 -- the SAME gate as the /personal-elins route. This panel is
            the cockpit twin of those sections and had the same defect: a
            forecast under a refusal. The physics response is already in the
            slice, so the gate costs no call. */}
        <SectionCollapseRisk elins={personal.elins} ep={personal.ep} />
        <SectionFieldWeather elins={personal.elins} ep={personal.ep} />
      </div>
    </section>
  );
}
